import { useForm } from "@tanstack/react-form";
import { useEffect } from "react";
import { Button } from "../../../components/Button";
import { Form } from "../../../components/Form";
import { FormGroup } from "../../../components/FormGroup";
import { FormLabel } from "../../../components/FormLabel";
import { FormActions } from "../../../components/FormActions";
import { SelectInput } from "../../../components/SelectInput";
import { Input } from "../../../components/Input";
import styled from "styled-components";
import colors from "@styling/colors";
import { spacing } from "@styling/sizing";
import { fontSizes } from "@styling/fonts";
import { useGetStoreQuery } from "../../../query/storesQuery";
import { IntegrationType } from "@KassaroAB/shared/types/enums/IntegrationType";
import { useTranslation } from "react-i18next";
import type {
  IntegrationWestpayCloud,
  RestCreateIntegration,
} from "@KassaroAB/shared/types/integrations/integration";
import type { StoreId } from "@KassaroAB/shared/types/ids";
import {
  useCreateIntegrationMutation,
  useUpdateIntegrationMutation,
} from "../../../query/integrationsQuery";

const ErrorMessage = styled.div`
  color: ${colors.danger};
  font-size: ${fontSizes.small}px;
  margin-top: ${spacing.x05}px;
`;

const PERCENTAGE_OPTIONS = [
  { value: 0.99, label: "0.99%" },
  { value: 0.89, label: "0.89%" },
  { value: 0.79, label: "0.79%" },
  { value: 0.69, label: "0.69%" },
  { value: 0.59, label: "0.59%" },
  { value: 0.49, label: "0.49%" },
];

interface WestpayFormProps {
  storeId: StoreId;
  integration?: IntegrationWestpayCloud;
  onSuccess: () => void;
  onError: () => void;
  onCancel: () => void;
}

export const WestpayForm = ({
  storeId,
  integration,
  onSuccess,
  onError,
  onCancel,
}: WestpayFormProps) => {
  const { t } = useTranslation();
  const { data: store } = useGetStoreQuery(storeId);
  const { mutate: createMutation } = useCreateIntegrationMutation(
    store?.chainId ?? null,
    storeId,
  );
  const { mutate: updateMutation } = useUpdateIntegrationMutation(store?.chainId ?? null);

  const form = useForm({
    defaultValues: {
      name:
        integration?.name ||
        (store ? t("integrationDetails.westpayDefaultName", { storeName: store.name }) : "Westpay"),
      percentageFee: integration?.percentageFee || 0.99,
      apiKey: integration?.apiKey || "",
    },
    onSubmit: ({ value }) => {
      const payload: RestCreateIntegration = {
        type: IntegrationType.WESTPAY_CLOUD,
        name: value.name,
        percentageFee: value.percentageFee,
        apiKey: value.apiKey,
      };
      if (integration) {
        updateMutation(
          { integrationId: integration.id, integration: payload },
          { onSuccess: () => onSuccess(), onError: () => onError() },
        );
      } else {
        createMutation(payload, {
          onSuccess: () => onSuccess(),
          onError: () => onError(),
        });
      }
    },
  });

  useEffect(() => {
    if (store && !integration) {
      form.setFieldValue(
        "name",
        t("integrationDetails.westpayDefaultName", { storeName: store.name }),
      );
    }
  }, [store, integration, form, t]);

  return (
    <Form
      onSubmit={(e) => {
        e.preventDefault();
        e.stopPropagation();
        form.handleSubmit();
      }}
    >
      <form.Field
        name="percentageFee"
        validators={{
          onChange: ({ value }) => (!value ? t("integrationDetails.percentageRequired") : undefined),
        }}
      >
        {(field) => (
          <FormGroup>
            <FormLabel>{t("integrationDetails.percentageLabel")}</FormLabel>
            <SelectInput
              value={field.state.value.toString()}
              onChange={(e) => field.handleChange(parseFloat(e.target.value))}
              onBlur={field.handleBlur}
              required
            >
              {PERCENTAGE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </SelectInput>
            {field.state.meta.errors.length > 0 && (
              <ErrorMessage>{field.state.meta.errors[0]}</ErrorMessage>
            )}
          </FormGroup>
        )}
      </form.Field>

      <form.Field
        name="apiKey"
        validators={{
          onChange: ({ value }) => (!value ? t("integrationDetails.apiKeyRequired") : undefined),
        }}
      >
        {(field) => (
          <FormGroup>
            <FormLabel>{t("integrationDetails.apiKeyLabel")}</FormLabel>
            <Input
              type="text"
              value={field.state.value}
              onChange={(e) => field.handleChange(e.target.value)}
              onBlur={field.handleBlur}
              required
              placeholder={t("integrationDetails.apiKeyPlaceholder")}
            />
            {field.state.meta.errors.length > 0 && (
              <ErrorMessage>{field.state.meta.errors[0]}</ErrorMessage>
            )}
          </FormGroup>
        )}
      </form.Field>

      <form.Field name="name">
        {(field) => (
          <FormGroup>
            <FormLabel>{t("integrationDetails.nameOptional")}</FormLabel>
            <Input
              type="text"
              value={field.state.value}
              onChange={(e) => field.handleChange(e.target.value)}
              onBlur={field.handleBlur}
              placeholder={t("integrationDetails.namePlaceholder")}
            />
          </FormGroup>
        )}
      </form.Field>

      <FormActions>
        <Button type="button" $color="secondary" onClick={onCancel}>
          {t("common.cancel")}
        </Button>
        <Button type="submit">{integration ? t("common.update") : t("common.create")}</Button>
      </FormActions>
    </Form>
  );
};
